import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError, type ListParams } from '../api';
import { EmptyState, ErrorBox, StatusChip } from '../components/Layout';
import { laoDateTime, money } from '../lib/money';
import { PROOF_LABEL, STATUS_LABEL, type PaymentList } from '../types';

/** ເງື່ອນໄຂຢູ່ໃນ URL ເພື່ອໃຫ້ກົດກັບຄືນ ຫຼື bookmark ໄດ້ */
function readParams(search: URLSearchParams): ListParams {
  return {
    status: search.get('status') ?? '',
    q: search.get('q') ?? '',
    from: search.get('from') ?? '',
    to: search.get('to') ?? '',
    page: Number(search.get('page') ?? '1') || 1,
  };
}

export function PaymentsPage() {
  const [search, setSearch] = useSearchParams();
  const params = readParams(search);
  const [data, setData] = useState<PaymentList | null>(null);
  const [error, setError] = useState<string | null>(null);

  const key = search.toString();
  useEffect(() => {
    let active = true;
    api
      .payments(readParams(new URLSearchParams(key)))
      .then((value) => {
        if (!active) return;
        setData(value);
        setError(null);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof ApiError ? err.message : 'ໂຫຼດຂໍ້ມູນບໍ່ໄດ້');
      });
    return () => {
      active = false;
    };
  }, [key]);

  function apply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const next = new URLSearchParams();
    for (const name of ['status', 'q', 'from', 'to']) {
      const value = String(form.get(name) ?? '').trim();
      if (value) next.set(name, value);
    }
    setSearch(next);
  }

  function goTo(page: number) {
    const next = new URLSearchParams(search);
    next.set('page', String(page));
    setSearch(next);
  }

  async function exportCsv() {
    try {
      await api.exportCsv(params);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'ດາວໂຫຼດບໍ່ສຳເລັດ');
    }
  }

  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const paid = data?.summary.find((s) => s.status === 'paid');

  return (
    <div className="page-fill">
      <div className="admin-head">
        <button type="button" className="link-button" onClick={exportCsv}>
          ດາວໂຫຼດ CSV
        </button>
      </div>

      <ErrorBox error={error} />

      <div className="card payments-panel">
        {/* key ບັງຄັບໃຫ້ form ອ່ານຄ່າໃໝ່ເມື່ອ URL ປ່ຽນ */}
        <form className="filters" onSubmit={apply} key={key}>
          <label>
            ສະແດງຕາມສະຖານະ
            <select name="status" defaultValue={params.status}>
              <option value="">ທັງໝົດ</option>
              {(['pending', 'paid', 'expired', 'cancelled'] as const).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          <label>
            ສະແດງຕາມວັນທີເລີ່ມຕົ້ນ
            <input name="from" type="date" defaultValue={params.from} />
          </label>
          <label>
            ສະແດງຕາມວັນທີສິ້ນສຸດ
            <input name="to" type="date" defaultValue={params.to} />
          </label>
          <label>
            ຄົ້ນຫາຕາມລາຍລະອຽດ
            <input name="q" defaultValue={params.q} placeholder="ເລກລາຍການ, ເລກບິນ, ລາຍລະອຽດ" />
          </label>
          <div className="filters-actions">
            <button type="submit" className="pay">
              ຄົ້ນຫາ
            </button>
          </div>
        </form>

        {data && (
          <>
            <div className="payments-results">
              {data.payments.length === 0 ? (
                <EmptyState message="ຍັງບໍ່ມີຂໍ້ມູນ" />
              ) : (
                <div className="table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>ສ້າງເມື່ອ</th>
                        <th>ລາຍລະອຽດ</th>
                        <th className="num">ຍອດ</th>
                        <th>ສະຖານະ</th>
                        <th>ຫຼັກຖານ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.map((p) => (
                        <tr key={p.paymentId}>
                          <td>
                            <Link to={`/payments/${p.paymentId}`}>{laoDateTime(p.createdAt)}</Link>
                          </td>
                          <td>
                            {p.description}
                            {p.orderNo && <span className="muted"> · {p.orderNo}</span>}
                          </td>
                          <td className="num">{money(p.amount)}</td>
                          <td>
                            <StatusChip status={p.status} label={STATUS_LABEL[p.status] ?? p.status} />
                          </td>
                          <td className="muted">
                            {p.proof ? (PROOF_LABEL[p.proof.source] ?? p.proof.source) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {pages > 1 && (
              <div className="pager">
                <button
                  type="button"
                  className="tab"
                  disabled={data.page <= 1}
                  onClick={() => goTo(data.page - 1)}
                >
                  ກ່ອນໜ້າ
                </button>
                <span className="muted">
                  ໜ້າ {data.page} / {pages}
                </span>
                <button
                  type="button"
                  className="tab"
                  disabled={data.page >= pages}
                  onClick={() => goTo(data.page + 1)}
                >
                  ຕໍ່ໄປ
                </button>
              </div>
            )}

            <p className="panel-footer">
              ພົບທັງໝົດ: {data.total} ລາຍການ
              {paid && ` · ຈ່າຍແລ້ວ ${paid.count} ລາຍການ ລວມ ${money(paid.total)}`}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
